import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Loader2, Plus, Search, Trash2, X, ReceiptText, Building2,
  AlertTriangle, CircleDollarSign, Cpu, ShieldAlert
} from 'lucide-react';
import { apiService } from '@/services/api';
import { useAuth } from '@/contexts/auth-hook';
import { podeAcessarVendas } from './vendas-acesso';
import {
  CotacaoResumida, CLASSE_STATUS_COTACAO, ROTULO_STATUS_COTACAO, StatusCotacao, moeda,
} from './cotacao-types';

const FILTROS: { valor: '' | StatusCotacao; rotulo: string }[] = [
  { valor: '', rotulo: 'Todas' },
  { valor: 'rascunho', rotulo: 'Rascunho' },
  { valor: 'em_andamento', rotulo: 'Em andamento' },
  { valor: 'finalizada', rotulo: 'Finalizadas' },
];

export function CotacoesListPage() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const temAcesso = podeAcessarVendas(user?.squadNome, isAdmin);

  const [cotacoes, setCotacoes] = useState<CotacaoResumida[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState<'' | StatusCotacao>('');
  const [erro, setErro] = useState<string | null>(null);

  const [mostrarNova, setMostrarNova] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [novaTitulo, setNovaTitulo] = useState('');
  const [novaCliente, setNovaCliente] = useState('');
  const [novaObra, setNovaObra] = useState('');
  // O admin não tem squad. Sem escolher um, a cotação nasceria sem dono e
  // ninguém do time a enxergaria — foi o que aconteceu na primeira cotação.
  const [squads, setSquads] = useState<{ id: string; nome: string }[]>([]);
  const [novaSquadId, setNovaSquadId] = useState('');

  const carregar = useCallback(async () => {
    try {
      const res = await apiService.listarCotacoes({ status: filtroStatus || undefined });
      if (res.success) {
        setCotacoes((res.data ?? []) as CotacaoResumida[]);
        setErro(null);
      } else {
        setErro(res.message || 'Não foi possível carregar as cotações.');
      }
    } catch {
      setErro('Não foi possível carregar as cotações.');
    } finally {
      setCarregando(false);
    }
  }, [filtroStatus]);

  useEffect(() => {
    if (!temAcesso) {
      setCarregando(false);
      return;
    }
    setCarregando(true);
    carregar();
  }, [carregar, temAcesso]);

  useEffect(() => {
    if (!isAdmin || !mostrarNova || squads.length > 0) return;
    apiService.listarSquads({ ativo: true }).then((res) => {
      if (res.success) setSquads((res.data ?? []) as { id: string; nome: string }[]);
    });
  }, [isAdmin, mostrarNova, squads.length]);

  // Busca é local: a lista de cotações de um squad cabe folgada na memória e
  // filtrar no cliente evita uma ida ao servidor a cada tecla.
  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return cotacoes;
    return cotacoes.filter((c) =>
      [c.numero, c.titulo, c.cliente, c.obra]
        .filter(Boolean)
        .some((campo) => String(campo).toLowerCase().includes(termo))
    );
  }, [cotacoes, busca]);

  const handleCriar = async () => {
    if (!novaTitulo.trim()) return;
    setSalvando(true);
    try {
      const res = await apiService.criarCotacao({
        titulo: novaTitulo.trim(),
        cliente: novaCliente.trim() || undefined,
        obra: novaObra.trim() || undefined,
        ...(isAdmin ? { squadId: novaSquadId || null } : {}),
      });
      if (res.success && res.data?.id) {
        navigate(`/vendas/cotacoes/${res.data.id}`);
      } else {
        setErro(res.message || 'Não foi possível criar a cotação.');
      }
    } catch {
      setErro('Não foi possível criar a cotação.');
    } finally {
      setSalvando(false);
    }
  };

  const handleRemover = async (cotacao: CotacaoResumida) => {
    const confirmado = window.confirm(
      `Excluir a cotação ${cotacao.numero} — "${cotacao.titulo}"?\n\n` +
      `As ${cotacao.totais.maquinas} máquina(s) importada(s), as fotos extraídas e os valores lançados serão apagados. Não há como desfazer.`
    );
    if (!confirmado) return;

    const res = await apiService.removerCotacao(cotacao.id);
    if (res.success) carregar();
    else setErro(res.message || 'Não foi possível excluir a cotação.');
  };

  if (!temAcesso) {
    return (
      <div className="p-8">
        <div className="max-w-lg mx-auto text-center bg-white dark:bg-schmersal-gray-darker rounded-xl p-8 shadow-sm">
          <ShieldAlert className="h-10 w-10 mx-auto text-schmersal-gray-medium mb-3" />
          <h1 className="text-lg font-bold text-schmersal-blue dark:text-white mb-1">Área restrita</h1>
          <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light">
            A área de Vendas está liberada para o squad MG. Fale com a administração se você precisa de acesso.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-schmersal-blue dark:text-white flex items-center gap-2">
            <ReceiptText className="h-6 w-6" />
            Cotações
          </h1>
          <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light mt-1">
            Orçamento de adequação a partir das Apreciações de Risco. Importe as ARs, o sistema separa
            as partes adequadas das não adequadas e você precifica só o que precisa de correção.
          </p>
        </div>
        <button
          onClick={() => setMostrarNova(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-schmersal-blue hover:bg-schmersal-blue-light text-white rounded-lg font-semibold text-sm transition-colors"
        >
          <Plus className="h-4 w-4" />
          Nova cotação
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-schmersal-gray-medium" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por número, título, cliente ou obra"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-schmersal-gray dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darker text-sm text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
          />
        </div>
        <div className="flex gap-1 p-1 rounded-lg bg-schmersal-gray-light dark:bg-schmersal-gray-darker">
          {FILTROS.map((f) => (
            <button
              key={f.valor || 'todas'}
              onClick={() => setFiltroStatus(f.valor)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                filtroStatus === f.valor
                  ? 'bg-white dark:bg-schmersal-blue text-schmersal-blue dark:text-white shadow-sm'
                  : 'text-schmersal-gray-dark dark:text-schmersal-gray-light hover:text-schmersal-blue'
              }`}
            >
              {f.rotulo}
            </button>
          ))}
        </div>
      </div>

      {erro && (
        <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{erro}</span>
        </div>
      )}

      {carregando ? (
        <div className="flex items-center justify-center py-20 text-schmersal-gray-medium">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Carregando cotações...
        </div>
      ) : filtradas.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-schmersal-gray-darker rounded-xl">
          <ReceiptText className="h-10 w-10 mx-auto text-schmersal-gray-medium mb-3" />
          <p className="font-semibold text-schmersal-blue dark:text-white">
            {cotacoes.length === 0 ? 'Nenhuma cotação ainda' : 'Nenhuma cotação para este filtro'}
          </p>
          <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light mt-1">
            {cotacoes.length === 0
              ? 'Crie a primeira e importe as Apreciações de Risco do cliente.'
              : 'Ajuste a busca ou o filtro de situação.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtradas.map((cotacao) => (
            <div
              key={cotacao.id}
              className="group bg-white dark:bg-schmersal-gray-darker rounded-xl border border-transparent hover:border-schmersal-turquoise/50 shadow-sm transition-colors"
            >
              <div className="flex flex-wrap items-center gap-4 p-4">
                <Link to={`/vendas/cotacoes/${cotacao.id}`} className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs px-2 py-0.5 rounded bg-schmersal-gray-light dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-schmersal-gray-light">
                      {cotacao.numero}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded border font-semibold ${CLASSE_STATUS_COTACAO[cotacao.status]}`}>
                      {ROTULO_STATUS_COTACAO[cotacao.status]}
                    </span>
                    {cotacao.totais.pendentes > 0 && (
                      <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 font-semibold">
                        {cotacao.totais.pendentes} sem preço
                      </span>
                    )}
                  </div>
                  <h2 className="font-bold text-schmersal-blue dark:text-white mt-1.5 group-hover:underline">
                    {cotacao.titulo}
                  </h2>
                  {(cotacao.cliente || cotacao.obra) && (
                    <p className="text-xs text-schmersal-gray-dark dark:text-schmersal-gray-light flex items-center gap-1 mt-0.5">
                      <Building2 className="h-3 w-3" />
                      {[cotacao.cliente, cotacao.obra].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </Link>

                <div className="flex items-center gap-6 text-sm">
                  <Indicador icone={Cpu} rotulo="máquinas" valor={cotacao.totais.maquinas} />
                  <Indicador icone={AlertTriangle} rotulo="a cotar" valor={cotacao.totais.naoAdequadas} />
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wide text-schmersal-gray-medium flex items-center gap-1 justify-end">
                      <CircleDollarSign className="h-3 w-3" /> total
                    </div>
                    <div className="font-bold text-schmersal-blue dark:text-schmersal-turquoise">
                      {moeda(cotacao.totais.valorTotal)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemover(cotacao)}
                    title="Excluir cotação"
                    className="p-2 rounded-lg text-schmersal-gray-medium hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {mostrarNova && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md bg-white dark:bg-schmersal-gray-darker rounded-xl shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
              <h2 className="font-bold text-schmersal-blue dark:text-white">Nova cotação</h2>
              <button onClick={() => setMostrarNova(false)} className="p-1 text-schmersal-gray-medium hover:text-schmersal-blue">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <Campo rotulo="Título *" valor={novaTitulo} onChange={setNovaTitulo} placeholder="Ex.: Adequação NR-12 — Margarina Uberlândia" autoFocus />
              <Campo rotulo="Cliente" valor={novaCliente} onChange={setNovaCliente} placeholder="Ex.: BRF S.A. Uberlândia" />
              <Campo rotulo="Obra / planta" valor={novaObra} onChange={setNovaObra} placeholder="Ex.: Mini fábrica de margarina" />
              {isAdmin && (
                <label className="block">
                  <span className="block text-xs font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light mb-1">
                    Squad responsável
                  </span>
                  <select
                    value={novaSquadId}
                    onChange={(e) => setNovaSquadId(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-schmersal-gray dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-sm text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
                  >
                    <option value="">Sem squad (só administradores veem)</option>
                    {squads.map((s) => (
                      <option key={s.id} value={s.id}>{s.nome}</option>
                    ))}
                  </select>
                  <span className="block text-[11px] text-schmersal-gray-medium mt-1">
                    Quem não é administrador só enxerga as cotações do próprio squad.
                  </span>
                </label>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark">
              <button
                onClick={() => setMostrarNova(false)}
                className="px-4 py-2 text-sm font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light hover:underline"
              >
                Cancelar
              </button>
              <button
                onClick={handleCriar}
                disabled={!novaTitulo.trim() || salvando}
                className="flex items-center gap-2 px-4 py-2 bg-schmersal-blue hover:bg-schmersal-blue-light disabled:opacity-50 text-white rounded-lg font-semibold text-sm transition-colors"
              >
                {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
                Criar e importar ARs
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Indicador({ icone: Icone, rotulo, valor }: { icone: typeof Cpu; rotulo: string; valor: number }) {
  return (
    <div className="text-right hidden sm:block">
      <div className="text-[10px] uppercase tracking-wide text-schmersal-gray-medium flex items-center gap-1 justify-end">
        <Icone className="h-3 w-3" /> {rotulo}
      </div>
      <div className="font-bold text-schmersal-gray-dark dark:text-white">{valor}</div>
    </div>
  );
}

function Campo({
  rotulo, valor, onChange, placeholder, autoFocus,
}: {
  rotulo: string;
  valor: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light mb-1">{rotulo}</span>
      <input
        value={valor}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-schmersal-gray dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-sm text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
      />
    </label>
  );
}
